import type { Conversation } from "@/types/conversation";

const MAX_CONVERSATIONS = 50;
export const CONVERSATIONS_KEY_PREFIX = "sam_conversations_";
export const CURRENT_CONVERSATION_KEY_PREFIX = "sam_current_conversation_";
// Unscoped key holding the user's recent chat inputs (written by the SAM chat
// components). Not enterprise-scoped, so it must be cleared explicitly on logout.
export const MESSAGE_HISTORY_KEY = "sam-message-history";
const getConversationsKey = (enterpriseId: string) =>
  `${CONVERSATIONS_KEY_PREFIX}${enterpriseId}`;
const getCurrentConversationKey = (enterpriseId: string) =>
  `${CURRENT_CONVERSATION_KEY_PREFIX}${enterpriseId}`;

export const chatStorage = {
  /**
   * Load all conversations from localStorage for a specific enterprise
   */
  loadConversations: (enterpriseId: string): Conversation[] => {
    try {
      const conversationsKey = getConversationsKey(enterpriseId);
      const savedConversations = localStorage.getItem(conversationsKey);
      if (savedConversations) {
        return JSON.parse(savedConversations, (key, value) => {
          // Revive Date objects
          if (
            (key === "timestamp" ||
              key === "createdAt" ||
              key === "updatedAt") &&
            typeof value === "string"
          ) {
            return new Date(value);
          }
          return value;
        });
      }
      return [];
    } catch (error) {
      console.error("Failed to load conversations from localStorage:", error);
      return [];
    }
  },

  /**
   * Save all conversations to localStorage for a specific enterprise
   * Only keeps the most recent MAX_CONVERSATIONS (50) conversations
   */
  saveConversations: (
    enterpriseId: string,
    conversations: Conversation[]
  ): void => {
    try {
      const conversationsKey = getConversationsKey(enterpriseId);
      // Limit to most recent 50 conversations (assuming conversations are already sorted by updatedAt desc)
      const limitedConversations = conversations.slice(0, MAX_CONVERSATIONS);
      localStorage.setItem(
        conversationsKey,
        JSON.stringify(limitedConversations)
      );
    } catch (error) {
      console.error("Failed to save conversations to localStorage:", error);
    }
  },

  /**
   * Load current conversation ID from localStorage
   */
  loadCurrentConversationId: (enterpriseId: string): string | null => {
    try {
      const currentKey = getCurrentConversationKey(enterpriseId);
      return localStorage.getItem(currentKey);
    } catch (error) {
      console.error(
        "Failed to load current conversation ID from localStorage:",
        error
      );
      return null;
    }
  },

  /**
   * Save current conversation ID to localStorage
   */
  saveCurrentConversationId: (
    enterpriseId: string,
    conversationId: string
  ): void => {
    try {
      const currentKey = getCurrentConversationKey(enterpriseId);
      localStorage.setItem(currentKey, conversationId);
    } catch (error) {
      console.error(
        "Failed to save current conversation ID to localStorage:",
        error
      );
    }
  },

  /**
   * Clear all conversation data for a specific enterprise
   */
  clearAllConversations: (enterpriseId: string): void => {
    try {
      const conversationsKey = getConversationsKey(enterpriseId);
      const currentKey = getCurrentConversationKey(enterpriseId);
      localStorage.removeItem(conversationsKey);
      localStorage.removeItem(currentKey);
    } catch (error) {
      console.error(
        "Failed to clear conversation data from localStorage:",
        error
      );
    }
  },

  /**
   * Remove ALL SAM chat data from localStorage: the (unscoped) recent-input
   * history plus every enterprise's stored conversations. Call this on logout
   * so one user's chat input/history can't leak to the next user on a shared
   * device. ENG-1569.
   */
  clearAllSamStorage: (): void => {
    if (typeof window === "undefined") {
      return;
    }
    try {
      localStorage.removeItem(MESSAGE_HISTORY_KEY);
      Object.keys(localStorage)
        .filter(
          (key) =>
            key.startsWith(CONVERSATIONS_KEY_PREFIX) ||
            key.startsWith(CURRENT_CONVERSATION_KEY_PREFIX)
        )
        .forEach((key) => localStorage.removeItem(key));
    } catch (error) {
      console.error("Failed to clear SAM storage from localStorage:", error);
    }
  },
};
